import type * as types from '../types';

import { LoggerPublicApi } from './LoggerPublicApi';

/**
 * The base class that all logger classes must extend.  This class incorporates both the logger
 * configuration, {@link LoggerConfiguration}, as well as the core logging API, {@link LoggerBase},
 * via the classes it inherits.
 */
export class Logger<T extends Logger<T>> extends LoggerPublicApi implements types.ILogger {
  public static create<I extends types.LoggerClass<T>, T extends Logger<T>>(
    this: I,
    options: types.InferredLoggerOptions<I>,
  ): types.InferredLoggerInstance<I>;

  public static create<I extends types.LoggerClass<T>, T extends Logger<T>>(
    this: I,
    name: string,
    options: types.InferredLoggerOptions<I>,
  ): types.InferredLoggerInstance<I>;

  /**
   * Creates a new logger instance with the provided name, {@link string}, and options,
   * {@link types.InferredLoggerOptions<I>}.
   *
   * The recommended way to create new logger instances is to configure an instance of
   * {@link LoggerConfig} and create logger instances via that {@link LoggerConfig} instance.
   * This approach allows common configuration to be shared across all loggers in an application
   * or package.  However, in cases where only a single logger instance is needed, this method
   * can be used to create a new logger instance with the provided options.
   *
   * @param {string} name The name of the logger instance to create.
   * @param {types.LoggerOptions<E>} options Options to configure the created logger instance with.
   */
  public static create<I extends types.LoggerClass<T>, T extends Logger<T>>(
    this: I,
    name: string | types.InferredLoggerOptions<I>,
    options?: types.InferredLoggerOptions<I>,
  ): types.InferredLoggerInstance<I> {
    if (typeof name === 'string') {
      return new this({ ...options, name }) as types.InferredLoggerInstance<I>;
    }
    return new this(name) as types.InferredLoggerInstance<I>;
  }

  /**
   * Adds the provided namespace, {@link types.Namespace}, to the logger instance.
   *
   * When a namespace is added to a logger instance, {@link Logger}, the logger instance is
   * attributed with a property that represents the child logger with the provided namespace name.
   *
   * @param {I} this The current logger instance for which the namespace should be added.
   * @param {N} namespace The namespace that should be added to the logger instance.
   *
   * @returns {types.MergeNamespace<N, I>}
   *   The logger instance with the provided namespace attributed as a property.
   */
  public addNamespace<I extends T, N extends types.Namespace>(
    this: I,
    namespace: N,
  ): types.MergeNamespace<N, I> {
    if (typeof namespace === 'string') {
      return Object.assign(this, {
        [namespace]: this.child(namespace),
      }) as types.MergeNamespace<N, I>;
    }
    return this.addNamespaces(namespace.children) as types.MergeNamespace<N, I>;
  }

  /**
   * Adds the provided namespaces, {@link types.Namespaces}, to the logger instance.
   *
   * When namespaces are added to a logger instance, {@link Logger}, the logger instance is
   * attributed with properties that represent child loggers with the provided namespace names.
   *
   * This is useful in cases where a logger instance for a given app or package in the monorepo
   * needs to pre-define the child logger instances it supports, so the child-parent logger
   * relationships can be more easily visualized in a single location and used to better understand
   * where log information is coming from in the eventual destination.
   *
   * @param {I} this The current logger instance for which the namespaces should be added.
   * @param {N} namespaces The namespaces that should be added to the logger instance.
   *
   * @returns {types.MergeNamespaces<N, I>}
   *   The logger instance with the provided namespaces attributed as properties.
   */
  public addNamespaces<I extends T, N extends types.Namespaces>(
    this: I,
    namespaces: N,
  ): types.MergeNamespaces<N, I> {
    /* eslint-disable-next-line @typescript-eslint/no-this-alias */
    let instance = this;
    for (const namespace of namespaces) {
      if (typeof namespace === 'string') {
        instance = Object.assign(instance, { [namespace]: instance.child(namespace) });
      } else {
        const child = instance.child(namespace.name) as T;
        instance = Object.assign(instance, {
          [namespace.name]: child.addNamespaces(namespace.children),
        });
      }
    }
    /* Safe Type Coercion: 'instance' is assembled at runtime to carry the namespace keys, but a
       loop over a generic tuple cannot express that shape to the compiler, so the return is
       asserted to the declared type. It is routed through 'unknown' because the simplified
       'MergeNamespaces' type makes a direct assertion read as a redundant no-op to
       'no-unnecessary-type-assertion', even though the compiler still requires it at this return.
       */
    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion */
    return instance as types.MergeNamespaces<N, I>;
  }

  /**
   * Creates a child logger instance with the provided name, {@link string}, and context.  The
   * child logger instance will inherit the configuration of the parent logger instance.
   */
  public child(name: string): Logger<T> {
    type Constructor = new (opts: types.LoggerOptions) => Logger<T>;
    const constructor = this.constructor as Constructor;
    return new constructor({ ...this.options, name: `${this.name}:${name}` });
  }
}
